import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';
import { of, Subscription } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';

import { StorageKey } from 'src/app/core/appsettings';
import { Client } from 'src/app/core/models/trainer.models';
import { DropFitnessSDKService } from 'src/app/core/services/dropfitnesssdk.service';
import { ToastService } from 'src/app/core/services/toast-service';
import { TrainerService } from 'src/app/core/services/trainer.service';
import { normalizePtSearchClientRecords } from 'src/app/core/utils/member-pt-search-normalize';
import { RosterBalanceStat, rosterBalanceStats } from 'src/app/core/utils/pt-client-balance-display';

export interface TrainerClientRow {
  id: number;
  name: string;
  avatar: string;
  stats: RosterBalanceStat[];
  isInactive: boolean;
}

@Component({
  selector: 'app-trainer-clients',
  templateUrl: './trainer-clients.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TrainerClientsComponent implements OnInit, OnDestroy {
  rows: TrainerClientRow[] = [];
  loading = false;
  private trainerId: number | null = null;
  private sub: Subscription | null = null;

  constructor(
    private readonly dfService: DropFitnessSDKService,
    private readonly trainerService: TrainerService,
    private readonly toastService: ToastService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.dfService.ensureUrls().then(() => {
      const raw = localStorage.getItem(StorageKey.CURRENT_USER);
      if (!raw) {
        this.toastService.showDanger('Trainer profile not found. Please sign in again.');
        this.cdr.markForCheck();
        return;
      }
      try {
        const trainer = JSON.parse(raw) as { Id?: number };
        const id = Number(trainer?.Id ?? 0);
        this.trainerId = Number.isFinite(id) && id > 0 ? id : null;
      } catch {
        this.trainerId = null;
      }
      if (this.trainerId == null) {
        this.toastService.showDanger('Trainer id missing. Cannot load roster.');
        this.cdr.markForCheck();
        return;
      }
      this.loadRoster();
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.sub = null;
  }

  trackRow(_index: number, row: TrainerClientRow): number {
    return row.id;
  }

  private loadRoster(): void {
    if (this.trainerId == null || this.trainerId <= 0) {
      return;
    }
    this.sub?.unsubscribe();
    this.loading = true;
    this.cdr.markForCheck();
    this.sub = of(this.trainerId)
      .pipe(
        switchMap((tid) =>
          this.trainerService.searchPtClients$(tid, '').pipe(
            map((raw) => normalizePtSearchClientRecords(raw)),
            catchError((err: unknown) => {
              const msg = typeof err === 'string' ? err : 'Could not load clients.';
              this.toastService.showDanger(msg);
              return of<Client[]>([]);
            }),
            finalize(() => {
              this.loading = false;
              this.cdr.markForCheck();
            })
          )
        )
      )
      .subscribe((clients) => {
        this.rows = clients.map((c) => this.toRow(c));
        this.cdr.markForCheck();
      });
  }

  private toRow(client: Client): TrainerClientRow {
    const p = client.Person;
    const name =
      (p?.FullName as string | undefined)?.trim() ||
      [p?.FirstName, p?.LastName].filter(Boolean).join(' ').trim() ||
      `Member ${client.Id}`;
    const avatar =
      (client.Avatar?.UploadedUrl as string | undefined)?.trim() ||
      'assets/images/users/user-dummy-img.jpg';
    return {
      id: client.Id,
      name,
      avatar,
      stats: rosterBalanceStats(client),
      isInactive: client.State !== 1
    };
  }
}
